import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet as WalletIcon, ArrowUpRight, ArrowDownRight, Gift } from "lucide-react";
import { API, inr } from "../lib/api";
import Seo from "../components/Seo";

const SRC_LABEL = { purchase: "Purchase Reward", referral: "Referral Reward", review: "Review Reward", club_welcome: "Club Welcome", redeem: "Used on Order", refund: "Refund" };

export default function WalletPage() {
  const [d, setD] = useState(null);
  useEffect(() => { API.get("/wallet").then((r) => setD(r.data)).catch(() => setD({ available: 0, pending: 0, used: 0, lifetime_earned: 0, transactions: [] })); }, []);
  if (!d) return <div className="max-w-4xl mx-auto px-4 py-20 text-stone-500">Loading wallet…</div>;

  const cards = [["Available", d.available, true], ["Pending", d.pending, false], ["Used", d.used, false], ["Lifetime Earned", d.lifetime_earned, false]];
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="wallet-page">
      <Seo title="Suvikka Wallet | Your Rewards" description="Track your Suvikka Wallet — earn rewards on purchases, referrals and reviews, and use them on future orders." canonicalPath="/wallet" noindex />
      <div className="flex items-center gap-3">
        <span className="inline-flex p-3 rounded-2xl bg-[#FFF0F4] text-[#6A1B29]"><WalletIcon className="w-6 h-6" /></span>
        <div>
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">Suvikka Wallet</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900">Your Rewards. Your Way.</h1>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        {cards.map(([l, v, hero]) => (
          <div key={l} className={`rounded-2xl p-5 border ${hero ? "bg-gradient-to-br from-[#6A1B29] to-[#501325] text-white border-transparent" : "bg-white border-stone-200"}`} data-testid={`wallet-${l.toLowerCase().replace(/\s/g, "-")}`}>
            <p className={`text-[11px] uppercase tracking-wider ${hero ? "text-white/80" : "text-stone-400"}`}>{l}</p>
            <p className={`font-display text-2xl sm:text-3xl font-bold mt-1 ${hero ? "text-white" : "text-stone-900"}`}>{inr(v)}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-6">
        <Link to="/drops" className="inline-flex items-center gap-2 bg-[#6A1B29] hover:bg-[#501325] text-white text-sm font-semibold px-6 py-3 rounded-full" data-testid="wallet-use">USE WALLET · SHOP</Link>
        <Link to="/club/refer" className="inline-flex items-center gap-2 border border-[#EADBD8] text-stone-800 text-sm font-semibold px-6 py-3 rounded-full hover:border-[#6A1B29]" data-testid="wallet-earn"><Gift className="w-4 h-4" /> EARN MORE</Link>
      </div>
      <p className="text-xs text-[#755C62] mt-3">💡 Use up to 10% of your next eligible order value from your wallet — automatically applied at checkout.</p>
      <h2 className="font-display text-xl font-semibold text-stone-900 mt-12 mb-4">Transactions</h2>
      <div className="bg-white border border-[#EADBD8] rounded-2xl divide-y divide-[#F3E7E4] shadow-soft" data-testid="wallet-transactions">
        {d.transactions.length === 0 && <p className="p-8 text-center text-stone-500">No wallet activity yet. Shop or refer a friend to start earning 💗</p>}
        {d.transactions.map((t, i) => {
          const debit = t.type === "redeem";
          return (
            <div key={i} className="flex items-center justify-between p-4" data-testid={`wallet-txn-${i}`}>
              <div className="flex items-center gap-3">
                <span className={`p-2 rounded-full ${debit ? "bg-stone-100 text-stone-500" : "bg-green-50 text-green-600"}`}>{debit ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}</span>
                <div>
                  <p className="text-sm font-medium text-stone-900">{SRC_LABEL[t.source || t.type] || "Reward"}</p>
                  <p className="text-xs text-stone-400">{t.note || t.order_id || ""} · {new Date(t.created_at).toLocaleDateString("en-IN")}</p>
                </div>
              </div>
              <p className={`font-semibold ${debit ? "text-stone-500" : "text-green-600"}`}>{debit ? "-" : "+"}{inr(t.amount)}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
